import type { SightmapComponent } from './schema.ts'

// Attributes the user or the app rewrites in place: the element's current state, not which instance it is.
const STATE_ATTRIBUTES = new Set(['checked', 'selected', 'disabled', 'open', 'hidden', 'data-state', 'aria-checked', 'aria-selected',
  'aria-pressed', 'aria-expanded', 'aria-disabled', 'aria-invalid', 'aria-current', 'aria-valuenow', 'aria-hidden'])
// Input types whose value attribute is authored markup, not entered content.
const STATIC_VALUE_INPUTS = new Set(['button', 'checkbox', 'hidden', 'image', 'radio', 'reset', 'submit'])
const TEXT_EDIT_ACTIONS = new Set(['fill', 'type'])

function entersValue(component: SightmapComponent): boolean {
  const structure = component.structure
  if (!structure) return !!component.actions?.some(action => TEXT_EDIT_ACTIONS.has(action) || action === 'select')
  return structure.tag === 'textarea' || structure.tag === 'select'
    || structure.tag === 'input' && !STATIC_VALUE_INPUTS.has(structure.type ?? 'text')
}

// An element whose own text content the user edits: a textarea, a textbox or contenteditable region.
function editableText(component: SightmapComponent): boolean {
  const selectors = Array.isArray(component.selector) ? component.selector : [component.selector]
  return component.structure?.tag === 'textarea' || component.structure?.role === 'textbox'
    || selectors.some(selector => selector.includes('contenteditable'))
}

// A control's text is edited content when the map fills it, unless it is an input or select: their
// text is the accessible name, never the entered value.
function editsText(component: SightmapComponent): boolean {
  const tag = component.structure?.tag
  return editableText(component)
    || !!component.actions?.some(action => TEXT_EDIT_ACTIONS.has(action)) && tag !== 'input' && tag !== 'select'
}

function containsEditableText(component: SightmapComponent): boolean {
  return (component.children ?? []).some(child => editableText(child) || containsEditableText(child))
}

function descendant(component: SightmapComponent, name: string): SightmapComponent | undefined {
  for (const child of component.children ?? []) {
    if (child.name === name) return child
    const nested = descendant(child, name)
    if (nested) return nested
  }
}

// Classifies every form of the property grammar (resolveSightmapProperties), following PATH.prop to the
// descendant property it reads.
function readsState(component: SightmapComponent, extract: string, seen = new Set<string>()): boolean {
  if (extract.startsWith('state:')) return true
  if (extract.startsWith('exists:')) return false
  if (extract === 'raw_text') return editsText(component)
  if (extract === 'text') return editsText(component) || containsEditableText(component)
  if (extract.startsWith('attr=')) {
    const attribute = extract.slice(5)
    return STATE_ATTRIBUTES.has(attribute) || attribute === 'value' && entersValue(component)
  }
  const parts = extract.split('.')
  if (parts.length < 2) return false
  const name = parts.pop()!
  let node: SightmapComponent | undefined = component
  for (const part of parts) node = node && descendant(node, part)
  if (!node) return false
  const declared = (node.properties ?? []).filter(property => property.name === name)
  if (!declared.length) return (name === 'text' || name === 'raw_text') && readsState(node, name, seen)
  const key = `${node.id ?? node.name}.${name}`
  if (seen.has(key)) return false
  seen.add(key)
  return declared.some(property => readsState(node, property.extract, seen))
}

/** Why a declared property can never identify an instance, or null when it can. */
export function nonIdentityReason(component: SightmapComponent, name: string): string | null {
  const extracts = (component.properties ?? []).filter(property => property.name === name).map(property => property.extract)
  if (component.params?.includes(name) && component.children?.length && extracts.includes('text')) return 'aggregate container text'
  if (extracts.some(extract => readsState(component, extract))) return 'mutable state'
  return null
}

/** Identity parameters the definition declares, minus container-wide text and mutable state. */
export function identityParameterNames(component: SightmapComponent): string[] {
  return (component.params ?? []).filter(name => !nonIdentityReason(component, name))
}
